import { useEffect, useState } from "react";

import { Option } from "@common/components/SelectDropdown/SelectDropdown";
import displayToast from "@common/components/Toast/displayToast";
import { useForm } from "@common/hooks/useForm";
import { Customer, Plan, PlanType } from "@common/models/billing";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { FREE_PLAN_SDK_INITS_LIMIT_STRING } from "@main/context/Subscription/SubscriptionContext";
import { useUpdateSelectedWorkspace } from "@main/layouts/views/WorkspaceRootLayout";

import { useAdministrationDeps } from "../Administration";
import { getPlanSubtitle } from "../components/SubscriptionCards/YourSubscriptionCard/YourSubscriptionCard";

export interface SubscriptionFormState {
  plan: Option;
  addOn: Option;
}

const defaultPlanAddOnValue = {
  label: `${FREE_PLAN_SDK_INITS_LIMIT_STRING} SDK initializations`,
  value: "",
  subtitle: "$0 forever",
} as Option;

const SubscriptionInitialState: SubscriptionFormState = {
  plan: { label: "Free plan", value: "", subtitle: "$0 forever" },
  addOn: defaultPlanAddOnValue,
};

interface Props {
  customer?: Customer;
  toggleChangeSubscription: () => void;
  plans: Plan[];
  addOns: Plan[];
}

export const useSubscriptionFormConsumer = ({ customer, toggleChangeSubscription, plans, addOns }: Props) => {
  const { selectedWorkspace } = useAppSelectionContext();
  const { administrationService } = useAdministrationDeps();
  const [loading, setLoading] = useState(false);
  const { updateSelectedWorkspace } = useUpdateSelectedWorkspace();

  const upgradeSubscription = async () => {
    if (!selectedWorkspace) return;

    try {
      setLoading(true);

      const plans =
        formState.addOn.value && formState.plan.value
          ? [formState.plan.value, formState.addOn.value]
          : formState.plan.value && !formState.addOn.value
          ? [formState.plan.value]
          : [formState.addOn.value];
      const res = await administrationService.createCheckoutSession(selectedWorkspace.id, plans);

      window.location.replace(res.data.stripe_checkout_session_url);

      await updateSelectedWorkspace(selectedWorkspace);
    } catch (error) {
      handleFormChange("plan", SubscriptionInitialState.plan);
      handleFormChange("addOn", SubscriptionInitialState.addOn);

      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const changeSubscription = async () => {
    if (!selectedWorkspace || !customer) return;

    try {
      setLoading(true);
      const plans =
        formState.addOn.value && formState.plan.value
          ? [formState.plan.value, formState.addOn.value]
          : formState.plan.value && !formState.addOn.value
          ? [formState.plan.value]
          : [formState.addOn.value];

      const res = await administrationService.changeSubscription(selectedWorkspace.id, plans);

      // `invoice_url` means Stripe is HOLDING the change until this invoice is paid — the card
      // needs 3DS/SCA, or it was declined. The workspace is still on its old plan and stays there
      // unless the admin pays, so send them straight to Stripe's payment page in this tab, exactly
      // as the new-subscription path above does.
      //
      // This used to `window.open(...)` a second tab after two awaits, which popup blockers stop,
      // and then showed the success toast regardless — so people were told they had switched when
      // they had not. (It also keyed off `status === PAST_DUE`, which the backend no longer
      // produces for this flow.)
      if (res.data.invoice_url) {
        window.location.assign(res.data.invoice_url);
        return;
      }

      await updateSelectedWorkspace(selectedWorkspace);

      displayToast({
        title: "Thank you for switching subscription",
        content: "You're in — now it's time to make the most out of Shake.",
      });

      toggleChangeSubscription();
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const { handleFormChange, formState, handleFormSubmit } = useForm<SubscriptionFormState>({
    initialState: SubscriptionInitialState,
    onSubmit: !Boolean(customer?.subscription) ? upgradeSubscription : changeSubscription,
    validators: {},
  });

  useEffect(() => {
    if (customer && customer.subscription?.items) {
      setLoading(true);

      const currentPlan = customer.subscription?.items.find((item) => item.plan.type === PlanType.PLAN)?.plan;

      if (currentPlan && plans.find((plan) => plan.id === currentPlan.id)) {
        handleFormChange("plan", getPlanAsOption(currentPlan));
      }

      const currentAddOn = customer.subscription?.items.find((item) => item.plan.type === PlanType.DEVICES)?.plan;
      if (!currentAddOn || !addOns.find((addOn) => addOn.id === currentAddOn.id)) {
        handleFormChange("addOn", defaultPlanAddOnValue);
      }
      if (currentAddOn) handleFormChange("addOn", getPlanAsOption(currentAddOn));

      setLoading(false);
    }
  }, [handleFormChange, customer, plans, addOns]);

  return { formState, handleFormChange, handleFormSubmit, loading };
};

const getPlanAsOption = (plan: Plan) => {
  return {
    label: plan.product_name,
    value: plan.id,
    subtitle: getPlanSubtitle(plan),
  } as Option;
};
